import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ClientEndpoints as Endpoints,
  type AddCommentRequest,
  type AddCommentResponse,
  type CopyClientToBranchRequest,
  type CopyClientToBranchResponse,
  type CreateClientByIntegrationRequest,
  type CreateClientByIntegrationResponse,
  type DeleteAvatarRequest,
  type DeleteAvatarResponse,
  type DeleteCommentRequest,
  type DeleteCommentResponse,
  type EditCommentRequest,
  type EditCommentResponse,
  type GetBranchesWithoutClientRequest,
  type GetBranchesWithoutClientResponse,
  type GetClientByIdRequest,
  type GetClientByIdResponse,
  type GetClientLeadInfoRequest,
  type GetClientLeadInfoResponse,
  type GetLeadClientForViewRequest,
  type GetLeadClientForViewResponse,
  type UpdateClientRequest,
  type UpdateClientResponse,
  type GetLeadCardRequest,
  type GetLeadCardResponse,
  type GetClientFieldValuesRequest,
  type GetClientFieldValuesResponse,
  type SetClientFieldValuesRequest,
  type SetClientFieldValuesResponse,
  type SetClientPhoneNumberRequest,
  type SetClientPhoneNumberResponse,
  type SetCompanyNameRequest,
  type SetCompanyNameResponse,
} from '@app/api/Client/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { CLIENT_CHUNK } from '@app/constants';

const LEAD_CARD_TAG = 'LeadCard';

export const clientApi2 = createApi({
  reducerPath: 'clientApi2',
  baseQuery: createApiBaseQuery(CLIENT_CHUNK),
  tagTypes: [LEAD_CARD_TAG],
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetClientById]: query<
      GetClientByIdResponse,
      GetClientByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetLeadClientForView]: query<
      GetLeadClientForViewResponse,
      GetLeadClientForViewRequest
    >({
      query: (params) => ({
        url: Endpoints.GetLeadClientForView,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchesWithoutClient]: query<
      GetBranchesWithoutClientResponse,
      GetBranchesWithoutClientRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchesWithoutClient,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CopyClientToBranch]: mutation<
      CopyClientToBranchResponse,
      CopyClientToBranchRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CopyClientToBranch,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateClient]: mutation<
      UpdateClientResponse,
      UpdateClientRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateClient,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.CreateClientByIntegration]: mutation<
      CreateClientByIntegrationResponse,
      CreateClientByIntegrationRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateClientByIntegration,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.AddComment]: mutation<AddCommentResponse, AddCommentRequest>({
      query: ({ data, ...params }) => ({
        url: 'Comments/Add',
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.EditComment]: mutation<EditCommentResponse, EditCommentRequest>({
      query: (params) => ({
        url: 'Comments/Edit',
        method: Methods.PUT,
        params,
      }),
    }),
    [Endpoints.DeleteComment]: mutation<
      DeleteCommentResponse,
      DeleteCommentRequest
    >({
      query: (params) => ({
        url: 'Comments/Delete',
        method: Methods.DELETE,
        params,
      }),
    }),

    [Endpoints.DeleteAvatar]: mutation<
      DeleteAvatarResponse,
      DeleteAvatarRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteAvatar,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.GetClientLeadInfo]: query<
      GetClientLeadInfoResponse,
      GetClientLeadInfoRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientLeadInfo,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetLeadCard]: query<GetLeadCardResponse, GetLeadCardRequest>({
      query: (params) => ({
        url: Endpoints.GetLeadCard,
        method: Methods.GET,
        params,
      }),
      // Настройки ИИ-функций филиала (в т.ч. авто-ответ) влияют на
      // IsAiAutoReply в ответе. Тег позволяет сбросить карточку из
      // мутации переключения ИИ-функций в другом api-слайсе.
      providesTags: [LEAD_CARD_TAG],
    }),
    [Endpoints.GetClientFieldValues]: query<
      GetClientFieldValuesResponse,
      GetClientFieldValuesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientFieldValues,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetClientFieldValues]: mutation<
      SetClientFieldValuesResponse,
      SetClientFieldValuesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetClientFieldValues,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetClientPhoneNumber]: mutation<
      SetClientPhoneNumberResponse,
      SetClientPhoneNumberRequest
    >({
      query: (params) => ({
        url: Endpoints.SetClientPhoneNumber,
        method: Methods.PATCH,
        params,
      }),
    }),
    [Endpoints.SetCompanyName]: mutation<
      SetCompanyNameResponse,
      SetCompanyNameRequest
    >({
      query: (params) => ({
        url: Endpoints.SetCompanyName,
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  useGetClientByIdQuery,
  useLazyGetClientByIdQuery,
  useGetLeadClientForViewQuery,
  useLazyGetLeadClientForViewQuery,
  useGetBranchesWithoutClientQuery,
  useLazyGetBranchesWithoutClientQuery,
  useCopyClientToBranchMutation,
  useUpdateClientMutation,
  useCreateClientByIntegrationMutation,
  useAddCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useDeleteAvatarMutation,
  useGetClientLeadInfoQuery,
  useLazyGetClientLeadInfoQuery,
  useGetLeadCardQuery,
  useLazyGetLeadCardQuery,
  useGetClientFieldValuesQuery,
  useLazyGetClientFieldValuesQuery,
  useSetClientFieldValuesMutation,
  useSetClientPhoneNumberMutation,
  useSetCompanyNameMutation,
} = clientApi2;
